import { useState, useEffect, useRef } from 'react'
import { Platform } from 'react-native'
import { UserServices } from '../../../../database/user.database'
import { useFooterApp } from '../../../redux/zustand/footerAppStore'

/**
 * useWebInitialLoad - Hook para manejar la carga inicial optimizada en web
 * Prioriza la carga del menú (footer_app) antes que otros recursos
 * Reporta progreso detallado para el loader
 */
export const useWebInitialLoad = () => {
    const [loadingState, setLoadingState] = useState({
        isLoading: true,
        progress: 0,
        message: 'Iniciando...',
        subMessage: null,
        footerLoaded: false,
        postsReady: false,
        layoutReady: false,
    })

    const footer_app = useFooterApp((state) => state.footer_app)
    const setFooterApp = useFooterApp((state) => state.setFooterApp)
    const clearFooterApp = useFooterApp((state) => state.clearFooterApp)
    const hasStarted = useRef(false)

    useEffect(() => {
        if (Platform.OS !== 'web' || hasStarted.current) return
        hasStarted.current = true

        const loadInitialData = async () => {
            try {
                console.log('[useWebInitialLoad] 🚀 Iniciando carga optimizada...')
                
                // Limpiar el footer_app anterior para evitar caché entre usuarios
                console.log('[useWebInitialLoad] 🧹 Limpiando footer_app previo del store')
                clearFooterApp()

                // FASE 1: Cargar footer_app PRIMERO (0-40%)
                setLoadingState(prev => ({
                    ...prev,
                    progress: 10,
                    message: 'Cargando menú...',
                    subMessage: 'Obteniendo configuración de navegación'
                }))

                const footerData = await UserServices.getFooterApp()
                
                if (footerData && Array.isArray(footerData) && footerData.length > 0) {
                    console.log('[useWebInitialLoad] ✅ Footer app cargado:', footerData.length, 'items')
                    setFooterApp(footerData)
                    
                    setLoadingState(prev => ({
                        ...prev,
                        progress: 40,
                        footerLoaded: true,
                        message: 'Menú cargado',
                        subMessage: `${footerData.length} secciones disponibles`
                    }))
                } else {
                    console.warn('[useWebInitialLoad] ⚠️ Footer app vacío, continuando...')
                    setLoadingState(prev => ({
                        ...prev,
                        progress: 40,
                        footerLoaded: true,
                        message: 'Configurando interfaz...',
                    }))
                }

                // FASE 2: Preparar layout (40-60%)
                setLoadingState(prev => ({
                    ...prev,
                    progress: 50,
                    message: 'Preparando interfaz...',
                    subMessage: 'Configurando componentes'
                }))

                // Pequeño delay para que el navegador renderice el menú
                await new Promise(resolve => setTimeout(resolve, 100))

                setLoadingState(prev => ({
                    ...prev,
                    progress: 60,
                    layoutReady: true,
                }))

                // FASE 3: Posts se cargan en paralelo (60-90%)
                setLoadingState(prev => ({
                    ...prev,
                    progress: 70,
                    message: 'Cargando contenido...',
                    subMessage: 'Preparando publicaciones'
                }))

                // Dar tiempo a que PostsList inicie su carga
                await new Promise(resolve => setTimeout(resolve, 300))

                setLoadingState(prev => ({
                    ...prev,
                    progress: 90,
                    postsReady: true,
                }))

                // FASE 4: Finalizar (90-100%)
                setLoadingState(prev => ({
                    ...prev,
                    progress: 95,
                    message: 'Casi listo...',
                    subMessage: null
                }))

                await new Promise(resolve => setTimeout(resolve, 200))

                setLoadingState(prev => ({
                    ...prev,
                    progress: 100,
                    isLoading: false,
                    message: 'Listo',
                }))

                console.log('[useWebInitialLoad] ✅ Carga inicial completada')

            } catch (error) {
                console.error('[useWebInitialLoad] ❌ Error en carga inicial:', error)
                // Continuar de todos modos
                setLoadingState(prev => ({
                    ...prev,
                    progress: 100,
                    isLoading: false,
                    message: 'Iniciado',
                    footerLoaded: true,
                    postsReady: true,
                    layoutReady: true,
                }))
            }
        }

        loadInitialData()
    }, [])

    // Auto-hide loader después de un tiempo máximo (failsafe)
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (loadingState.isLoading) {
                console.warn('[useWebInitialLoad] ⏱️ Timeout alcanzado, ocultando loader')
                setLoadingState(prev => ({
                    ...prev,
                    progress: 100,
                    isLoading: false,
                }))
            }
        }, 10000) // 10 segundos máximo

        return () => clearTimeout(timeout)
    }, [loadingState.isLoading])

    return loadingState
}
